import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { SignInProviders, oauthErrorMessage } from "../components/SignInProviders";
import { useRedirectTarget } from "../lib/navigation";
import { ApiError, api } from "../lib/api";
import { useSession } from "../lib/session";

export default function SignIn() {
  const { applyStep, settings } = useSession();
  const navigate = useNavigate();
  const from = useRedirectTarget();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A provider sign-in that came back with an error lands here with ?oauth_error=.
  const [error, setError] = useState<string | null>(() => oauthErrorMessage(params.get("oauth_error")));
  const providers = settings.signInProviders ?? [];
  const [busy, setBusy] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.login({ email, password });
      applyStep(result.step, result.mfaEnrolled);
      navigate(result.mfaEnrolled ? "/mfa" : "/mfa/set-up", {
        replace: true,
        state: from ? { from } : undefined,
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Sign-in failed. Try again.");
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      heading="Sign in"
      blurb={providers.length
        ? "Your password or a linked account first, then the code from your authenticator app."
        : "Your password first, then the code from your authenticator app."}
    >
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}

      {providers.length ? (
        <>
          <SignInProviders providers={providers} query={{ from: from ?? undefined }} />
          <p className="auth-or">or with your email</p>
        </>
      ) : null}

      <form className="stack" onSubmit={onSubmit} noValidate>

        <div className="field">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            className="input"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            className="input"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? <span className="spinner" /> : null}
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <div className="auth-alt">
        <Link to="/forgot-password">Forgotten your password?</Link>
        {settings.allowSignup ? (
          <Link to="/register">Create an account</Link>
        ) : (
          <span className="dim">New here? You need an invitation.</span>
        )}
      </div>
    </AuthLayout>
  );
}
